import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, AlertTriangle, Award, Download, RefreshCw, LayoutDashboard, Sparkles, BarChart3 } from 'lucide-react';
import { getHrSummary, getHrTopPerformers, getHrRiskSignals } from '../api/dashboard';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import StatCard from '../components/charts/StatCard';
import RadarChart from '../components/charts/RadarChart';
import BarChart from '../components/charts/BarChart';
import PredictiveInsights from '../components/analytics/PredictiveInsights';
import ActivityHeatmap from '../components/analytics/ActivityHeatmap';
import toast from 'react-hot-toast';

const HrDashboardPageEnhanced = () => {
  const [summary, setSummary] = useState(null);
  const [topPerformers, setTopPerformers] = useState([]);
  const [riskSignals, setRiskSignals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const [summaryData, performersData, riskData] = await Promise.all([
        getHrSummary(),
        getHrTopPerformers(10),
        getHrRiskSignals()
      ]);
      
      setSummary(summaryData.data);
      setTopPerformers(performersData.data);
      setRiskSignals(riskData.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load HR dashboard data:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const handleRefresh = () => {
    loadDashboardData(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Prepare BIG FIVE radar chart data
  const bigFiveData = [
    { trait: 'Openness', score: summary?.avgOpennessScore || 0 },
    { trait: 'Conscientiousness', score: summary?.avgConscientiousnessScore || 0 },
    { trait: 'Extraversion', score: summary?.avgExtraversionScore || 0 },
    { trait: 'Agreeableness', score: summary?.avgAgreeablenessScore || 0 },
    { trait: 'Neuroticism', score: summary?.avgNeuroticismScore || 0 }
  ];

  // Prepare top performers bar chart data
  const performersChartData = topPerformers.slice(0, 8).map(p => ({
    name: p.name.split(' ')[0],
    score: p.overallScore
  }));

  // Risk signals data
  const totalRisks = (riskSignals?.highStressRisk || 0) + (riskSignals?.engagementRisk || 0) + (riskSignals?.lowPerformanceRisk || 0);

  return (
    <div>
      {/* Header */}
      <motion.div 
        className="mb-8 flex items-center justify-between"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <LayoutDashboard className="w-6 h-6 mr-2 text-cobalt-600" />
          HR Dashboard
        </h1>
        <div className="flex items-center space-x-2">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-cobalt-50 text-cobalt-600 rounded-lg hover:bg-cobalt-100 transition-all disabled:opacity-50 font-medium"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </motion.div>

      {/* Enhanced KPI Cards with Trends */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatCard
            icon={Users}
            label="Candidates Tested"
            value={summary?.totalCandidatesTested || 0}
            trend={summary?.trends?.candidates}
            color="blue"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatCard
            icon={TrendingUp}
            label="Avg Productivity"
            value={summary?.avgProductivityScore || 0}
            trend={summary?.trends?.productivity}
            color="green"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StatCard
            icon={Award}
            label="Top Performers"
            value={summary?.topPerformersCount || 0}
            trend={summary?.trends?.topPerformers}
            color="purple"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StatCard
            icon={AlertTriangle}
            label="Risk Signals"
            value={totalRisks}
            trend={summary?.trends?.riskSignals}
            color="orange"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <StatCard
            icon={Users}
            label="Openness Avg"
            value={summary?.avgOpennessScore || 0}
            color="blue"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <StatCard
            icon={BarChart3}
            label="Extraversion Avg"
            value={summary?.avgExtraversionScore || 0}
            color="green"
          />
        </motion.div>
      </div>

      {/* Charts Row 1: BIG FIVE Radar & Top Performers */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Personality & Performance Analytics</h2>
        <p className="text-sm text-gray-500">BIG FIVE traits and top performers leaderboard</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* BIG FIVE Radar Chart */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-50 to-indigo-100">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Average BIG FIVE Personality Traits</h3>
              </div>
              <RadarChart
                data={bigFiveData}
                dataKey="score"
                name="Team Average"
                color="#8b5cf6"
                height={200}
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Top Performers Bar Chart */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-green-50 to-emerald-100">
                    <Award className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Top Performers Leaderboard</h3>
                </div>
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center">
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </button>
              </div>
              <BarChart
                data={performersChartData}
                dataKeys={[{ dataKey: 'score', name: 'Score' }]}
                colors={['#10b981']}
                height={200}
                horizontal
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row 2: Risk Signals & Top Performers List */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Risk Analysis & Detailed Rankings</h2>
        <p className="text-sm text-gray-500">Risk indicators and performer details</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Risk Signals Breakdown */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-red-50 to-orange-100">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Risk Signals Analysis</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">High Stress Risk</span>
                    <span className="text-lg font-bold text-red-600">{riskSignals?.highStressRisk || 0}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (riskSignals?.highStressRisk || 0) * 10)}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Engagement Risk</span>
                    <span className="text-lg font-bold text-orange-600">{riskSignals?.engagementRisk || 0}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-orange-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (riskSignals?.engagementRisk || 0) * 10)}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Low Performance Risk</span>
                    <span className="text-lg font-bold text-yellow-600">{riskSignals?.lowPerformanceRisk || 0}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (riskSignals?.lowPerformanceRisk || 0) * 10)}%` }}
                    />
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">💡 Recommendation</p>
                  <p className="text-sm text-blue-700 mt-1">
                    {totalRisks > 5 
                      ? 'Consider implementing stress management programs and team engagement activities.'
                      : 'Team health is good. Continue monitoring and maintain current practices.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Top Performers Detailed List */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-100">
                  <Award className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Top Performers Details</h3>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {topPerformers.map((performer, index) => (
                  <div key={performer.userId} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold text-sm">
                        #{index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{performer.name}</p>
                        <p className="text-xs text-gray-500">{performer.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600 text-lg">{performer.overallScore}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(performer.completedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Advanced Analytics Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 1.1 }}
        className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 -mx-2"
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-700 flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
            AI-Powered Talent Insights
          </h2>
          <p className="text-xs text-gray-500 mt-1">Predictive insights and intelligent recommendations</p>
        </div>
        <div className="space-y-4">
          <PredictiveInsights data={summary} />
          <ActivityHeatmap />
        </div>
      </motion.div>
    </div>
  );
};

export default HrDashboardPageEnhanced;
